import { useSyncExternalStore } from "react";

const STORAGE_KEY = "seismic:contextual-tour-store";

const defaultState = {
  tour: {
    active: false,
    minimized: false,
    selectedFeatureIndex: 0,
    featureIndexByRoute: {},
    pendingFeatureIndex: null,
    lastRoute: "/",
    panelPosition: null,
    platformStepIndex: 0,
    routeHistory: [],
  },
};

const canUseStorage = () =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const readState = () => {
  if (!canUseStorage()) return defaultState;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;

    const parsed = JSON.parse(raw);
    return {
      ...defaultState,
      ...parsed,
      tour: {
        ...defaultState.tour,
        ...(parsed.tour || {}),
      },
    };
  } catch (error) {
    console.error("Failed to read contextual tour store:", error);
    return defaultState;
  }
};

let state = readState();
const listeners = new Set();

const persist = () => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Failed to persist contextual tour store:", error);
  }
};

const emit = () => {
  persist();
  listeners.forEach((listener) => listener());
};

export const getContextualTourState = () => state;

export const setContextualTourState = (updater) => {
  const nextState =
    typeof updater === "function" ? updater(state) : { ...state, ...updater };

  state = {
    ...defaultState,
    ...nextState,
    tour: {
      ...defaultState.tour,
      ...(nextState.tour || {}),
    },
  };

  emit();
  return state;
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useContextualTourStore = (selector = (currentState) => currentState) =>
  useSyncExternalStore(subscribe, () => selector(state), () => selector(defaultState));

export const contextualTourActions = {
  startTour(route = "/", platformStepIndex = 0) {
    return setContextualTourState((currentState) => ({
      ...currentState,
      tour: {
        ...currentState.tour,
        active: true,
        minimized: false,
        platformStepIndex: Math.max(0, Number(platformStepIndex) || 0),
        panelPosition: null,
        routeHistory: [route],
        selectedFeatureIndex: 0,
        pendingFeatureIndex: null,
        lastRoute: route,
        featureIndexByRoute: {},
      },
    }));
  },

  stopTour() {
    return setContextualTourState((currentState) => ({
      ...currentState,
      tour: {
        ...currentState.tour,
        active: false,
        minimized: false,
        selectedFeatureIndex: 0,
        routeHistory: [],
      },
    }));
  },

  setRoute(route) {
    return setContextualTourState((currentState) => {
      const history = [...(currentState.tour.routeHistory || [])];
      const last = history[history.length - 1];
      if (route && route !== last) {
        history.push(route);
      }

      const lastRoute = currentState.tour.lastRoute || "/";
      const savedIndex = currentState.tour.selectedFeatureIndex || 0;
      const pending = currentState.tour.pendingFeatureIndex;

      let nextIndex = savedIndex;
      if (route && route !== lastRoute) {
        nextIndex =
          pending?.route === route
            ? Math.max(0, Number(pending.index) || 0)
            : 0;
      }

      return {
        ...currentState,
        tour: {
          ...currentState.tour,
          lastRoute: route,
          routeHistory: history,
          selectedFeatureIndex: nextIndex,
          pendingFeatureIndex: null,
        },
      };
    });
  },

  primeFeatureIndex(route, index) {
    const nextIndex = Math.max(0, Number(index) || 0);
    return setContextualTourState((currentState) => ({
      ...currentState,
      tour: {
        ...currentState.tour,
        pendingFeatureIndex: { route, index: nextIndex },
        ...(currentState.tour.lastRoute === route
          ? { selectedFeatureIndex: nextIndex }
          : {}),
      },
    }));
  },

  setSelectedFeatureIndex(index) {
    return setContextualTourState((currentState) => {
      const nextIndex = Math.max(0, Number(index) || 0);

      return {
        ...currentState,
        tour: {
          ...currentState.tour,
          selectedFeatureIndex: nextIndex,
          pendingFeatureIndex: null,
        },
      };
    });
  },

  setMinimized(minimized) {
    return setContextualTourState((currentState) => ({
      ...currentState,
      tour: {
        ...currentState.tour,
        minimized: Boolean(minimized),
      },
    }));
  },

  setPanelPosition(panelPosition) {
    return setContextualTourState((currentState) => ({
      ...currentState,
      tour: {
        ...currentState.tour,
        panelPosition,
      },
    }));
  },

  setPlatformStepIndex(index) {
    return setContextualTourState((currentState) => {
      const nextIndex = Math.max(0, Number(index) || 0);
      const prevIndex = currentState.tour.platformStepIndex || 0;

      return {
        ...currentState,
        tour: {
          ...currentState.tour,
          platformStepIndex: nextIndex,
          ...(nextIndex !== prevIndex && !currentState.tour.pendingFeatureIndex
            ? { selectedFeatureIndex: 0 }
            : {}),
        },
      };
    });
  },

  goBackInHistory() {
    return setContextualTourState((currentState) => {
      const history = [...(currentState.tour.routeHistory || [])];
      if (history.length < 2) return currentState;
      history.pop();
      return {
        ...currentState,
        tour: {
          ...currentState.tour,
          routeHistory: history,
        },
      };
    });
  },
};
